import { useEffect, useRef, useState, FocusEvent } from 'react'

import {
    Box,
    Flex,
    FormControl,
    Image,
    Input,
    InputGroup,
    InputLeftElement,
    List,
    ListItem,
    Text,
    useOutsideClick,
} from '@chakra-ui/react'
import { useFormikContext } from 'formik'

import { IconChevron } from '@core/assets/icons/chevron'
import { LangIcon } from '@core/assets/icons/lang'
import CountryIcon from '@core/components/ApplyDialogV2/ApplyV2ProfileStep/PhoneInput/CountryIcon'
import { prefixes } from '@core/utils/helpers/prefixes'

export type FlagOption = {
    prefix: string
    code: string
}

export type Props = {
    onChange: (args: string) => void
    onBlur?: (e: FocusEvent<any>) => void
    type: 'mobile' | 'office' | 'home' | 'work' | 'default'
    exist?: () => void
    name?: string
}

export type FormValues = {
    mobilePhone: {
        prefix: string
        number: string
    }
    homePhone?: {
        prefix: string
        number: string
    }
    officePhone?: {
        prefix: string
        number: string
    }
    workPhone?: {
        prefix: string
        number: string
    }
    phone: {
        prefix: string
        number: string
    }
}

export const FlagSelect = ({ onChange, type, exist, name, onBlur }: Props) => {
    const { values, errors, touched } = useFormikContext<FormValues>()
    const [isDropdownOpened, setIsDropdownOpened] = useState<boolean>(false)
    const [selectedItem, setSelectedItem] = useState<FlagOption | undefined>(undefined)
    const [value, setValue] = useState<string | undefined>(undefined)
    const [data, setData] = useState<FlagOption[] | undefined>(undefined)
    const [filteredData, setFilteredData] = useState<FlagOption[] | undefined>(undefined)
    const ref: any = useRef()

    useOutsideClick({
        ref: ref,
        handler: () => setIsDropdownOpened(false),
    })

    const handlerСodeInput = (value: string) => {
        onChange(value)
        setValue(value)
        filterData(value)
        if (!value) {
            setSelectedItem(undefined)
        } else {
            data?.length &&
                data.map((option: FlagOption) => {
                    if (option.prefix === value) {
                        setSelectedItem(option)
                    }
                })
        }
    }

    const filterData = (value: string) => {
        if (value) {
            const filtered = data?.filter((item) => item.prefix.includes(value))
            setFilteredData(filtered)
        } else {
            setFilteredData(data)
        }
    }

    useEffect(() => {
        if (!value) {
            setSelectedItem(undefined)
        }
        if (data?.length) {
            data?.map((flag: FlagOption) => {
                if (flag.prefix === value) {
                    setSelectedItem(flag)
                    exist && exist()
                }
            })
        }
    }, [value, data, type])

    useEffect(() => {
        setValue(selectedItem?.prefix)
    }, [selectedItem])

    useEffect(() => {
        if (prefixes) {
            const data = prefixes.map((item: FlagOption) => ({
                prefix: item.prefix.substring(1),
                code: item.code,
            }))
            setData(data)
            setFilteredData(data)
        }
    }, [prefixes])

    useEffect(() => {
        const typeToValueMap = {
            mobile: values.mobilePhone?.prefix,
            home: values.homePhone?.prefix,
            office: values.officePhone?.prefix,
            work: values.workPhone?.prefix,
            default: values.phone?.prefix,
        }

        setValue(typeToValueMap[type])
    }, [type, values])

    const baseUrl = process.env.ENV === 'local' ? process.env.API_URL : window.location.origin
    const selectedFlagSrc = selectedItem?.code && baseUrl ? `${baseUrl}/${selectedItem.code}` : null
    const getCountryIcon = (item?: FlagOption | null) => {
        const itemFlagSrc = item?.code && baseUrl ? `${baseUrl}/${item.code}` : null
        if (item?.prefix?.includes('966')) return <CountryIcon flag={'saudi-arabia'} />
        // if (item?.prefix?.includes('852')) return <CountryIcon flag={'hong-kong'} />
        if (itemFlagSrc) return <Image src={itemFlagSrc} maxW={4} />
        return <LangIcon w={'20'} h={'20'} />
    }

    return (
        <FormControl>
            <InputGroup
                w={'full'}
                minW={28}
                borderColor={
                    (!!errors.mobilePhone?.prefix && touched.mobilePhone?.prefix) ||
                    (!!errors.phone?.prefix && touched.phone?.prefix)
                        ? 'formElements.borderError'
                        : 'formElements.borderDefault'
                }
            >
                <InputLeftElement
                    w={'52px'}
                    cursor={'pointer'}
                    border={'1px solid'}
                    borderLeftRadius={'4px'}
                    borderTopRadius={'4px'}
                    borderBottomRadius={'4px'}
                    borderColor={
                        (!!errors.mobilePhone?.prefix && touched.mobilePhone?.prefix) ||
                        (!!errors.phone?.prefix && touched.phone?.prefix)
                            ? 'formElements.borderError'
                            : 'formElements.borderDefault'
                    }
                    onClick={() => setIsDropdownOpened((prev) => !prev)}
                >
                    <Text as={'span'} w={5} h={5} mr={1.5}>
                        {getCountryIcon(selectedItem)}
                        {/*{selectedItem?.code ? (*/}
                        {/*    <Image*/}
                        {/*        src={`${process.env.ENV === 'local' ? process.env.API_URL : window.location.origin}/${*/}
                        {/*            selectedItem?.code*/}
                        {/*        }`}*/}
                        {/*    />*/}
                        {/*) : (*/}
                        {/*    <LangIcon w={'20'} h={'20'} />*/}
                        {/*)}*/}
                    </Text>
                    {isDropdownOpened ? (
                        <Box transform={'rotate(180deg)'}>
                            <IconChevron width={2} height={1} strokeColor={'#2F80ED'} />
                        </Box>
                    ) : (
                        <IconChevron width={2} height={1} strokeColor={'#2F80ED'} />
                    )}
                </InputLeftElement>
                <Flex position={'relative'}>
                    <Text
                        as={'span'}
                        position={'absolute'}
                        left={'63px'}
                        top={'50%'}
                        transform={'translateY(-50%)'}
                        color={!value?.trim().length ? 'GrayText' : 'formElements.placeholder'}
                        zIndex={1}
                        fontSize={'sm'}
                    >
                        +
                    </Text>
                    <Input
                        pl={'70px'}
                        pr={2}
                        name={name}
                        type={'number'}
                        borderRight={'1px solid'}
                        borderBottom={'1px solid'}
                        borderTop={'1px solid'}
                        borderColor={
                            (!!errors.mobilePhone?.prefix && touched.mobilePhone?.prefix) ||
                            (!!errors.phone?.prefix && touched.phone?.prefix)
                                ? 'formElements.borderError'
                                : 'formElements.borderDefault'
                        }
                        value={value}
                        onChange={(e) => handlerСodeInput(e.target.value)}
                        onBlur={onBlur}
                    />
                </Flex>
            </InputGroup>
            {isDropdownOpened ? (
                <Box
                    my={1}
                    bg={'text.white'}
                    width={28}
                    zIndex={'dropdown'}
                    position={'absolute'}
                    borderRadius={'4px'}
                    overflow={'auto'}
                    boxShadow={'0px 8px 12px rgba(0, 0, 0, 0.08)'}
                    ref={ref}
                >
                    <List maxH={'200px'}>
                        {filteredData?.map((item: FlagOption, index: number) => {
                            const itemFlagSrc = item.code && baseUrl ? `${baseUrl}/${item.code}` : null

                            return (
                                <ListItem
                                    key={index}
                                    paddingY={2}
                                    color={'text.secondary'}
                                    cursor={'pointer'}
                                    fontWeight={'normal'}
                                    fontSize={'sm'}
                                    onClick={() => {
                                        onChange(item.prefix)
                                        setSelectedItem(item)
                                        setIsDropdownOpened(false)
                                    }}
                                    display={'flex'}
                                    alignItems={'center'}
                                    style={{ transition: 'all .2s ease-in-out' }}
                                    _hover={{ bg: 'bg.3', color: 'text.secondary' }}
                                    sx={
                                        item?.prefix === selectedItem?.prefix
                                            ? {
                                                  backgroundColor: 'bg.3',
                                                  color: 'text.secondary',
                                              }
                                            : {}
                                    }
                                >
                                    <Flex w={'52px'} justifyContent={'center'}>
                                        {getCountryIcon(item)}
                                    </Flex>
                                    <Text as="span" pl={3}>
                                        {item?.prefix}
                                    </Text>
                                </ListItem>
                            )
                        })}
                    </List>
                </Box>
            ) : null}
        </FormControl>
    )
}
